import React from "react";
import { Row, Collapse, Alert, Skeleton } from "antd";
import { CaretRightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import moment from "moment";
import InfiniteScroll from "react-infinite-scroller";
import useInfiniteQuery from "hooks/useInfiniteQuery";

// custom components
import ClassList from "common/class_card/ClassList";

// types
import { Lesson, CategoryState, CityState, DayState } from "types";

// queries
import GET_SEARCH_RESULTS from "graphql/queries/GetSearchResults";

// styling
import { CollapsedSearchResult } from "./ClassSearchResultStyle";

const { Panel } = Collapse;

type ClassSearchresultprops = {
  filter: any;
};

const DEFAULT_PAGE_SIZE = 3;

function ClassSearchResult(props: ClassSearchresultprops) {
  const { t } = useTranslation("book_a_class");

  const { filter } = props;
  // slider second number has some issue
  const updateFilterTime = [filter.time[0], filter.time[1] + 1];

  const searchParams = {
    levelId: filter.level.id,
    classType: filter.classType,
    days: filter.days
      .filter((day: DayState) => day.chosen)
      .map((day: DayState) => day.name),
    time: updateFilterTime,
    includeStudiedTopics: filter.includeStudiedTopics,
    excludeAllCityVerion: filter.excludeAllCityVerion,
    categoryIds: filter.categories
      .filter((category: CategoryState) => category.chosen)
      .map((category: CategoryState) => category.id),
    spokenOnly: filter.spokenOnly,
    numberOfTopicsAhead: filter.numberOfTopicsAhead,
    characters: filter.characters,
    cities: filter.cities
      .filter((city: CityState) => city.chosen)
      .map((city: CityState) => city.name),
    first: DEFAULT_PAGE_SIZE,
  };

  const {
    nodes: classSearchResults,
    error,
    pageInfo,
    loading,
    loadMore,
  } = useInfiniteQuery("classSearchResults", GET_SEARCH_RESULTS, {
    variables: searchParams,
    skip: filter.level.id === "0",
  });

  if (error) {
    return (
      <Alert
        type="error"
        message={t(
          "Error loading classes. Please check your internet or let us know if this keeps happening."
        )}
      />
    );
  }

  if (loading) {
    return <Alert type="info" message={t("Loading...")} />;
  }

  let classSearchResultsImp;
  if (classSearchResults) {
    const classSearchResultsObj = classSearchResults.reduce(
      (accumulator, classSearch) => {
        return {
          ...accumulator,
          [classSearch.date]: classSearch,
        };
      },
      {}
    );

    classSearchResultsImp = Object.keys(classSearchResultsObj)
      .sort((dateA, dateB) => {
        return new Date(dateA).getTime() - new Date(dateB).getTime();
      })
      .map((key) => classSearchResultsObj[key]);
  }

  if (!classSearchResultsImp) {
    return null;
  }

  if (!classSearchResultsImp || classSearchResultsImp.length === 0) {
    return (
      <Alert
        type="warning"
        message={t(
          'You already studied or skipped all topics in this level for the filters chosen. Change your filter selection above to see more lessons. You can find additional filters by clicking "More". Choose "Include studied topics" under "More" filters to also see lessons with topics you previously studied or skipped.'
        )}
      />
    );
  }

  return (
    <InfiniteScroll
      pageStart={0}
      loadMore={loadMore}
      hasMore={pageInfo?.hasNextPage}
      loader={<Skeleton active key={pageInfo?.startCursor} />}
    >
      <CollapsedSearchResult
        bordered={false}
        defaultActiveKey={[classSearchResultsImp[0].date]}
        expandIcon={() => <CaretRightOutlined />}
      >
        {classSearchResultsImp.map(
          (day: { date: string; lessons: Lesson[] }) => (
            <Panel header={moment(day.date).format("LL - ddd")} key={day.date}>
              <Row gutter={24}>
                <ClassList
                  lessons={day.lessons}
                  section="filter-result"
                  allowsBooking
                  hidesDate
                  allowsStacking
                />
              </Row>
            </Panel>
          )
        )}
      </CollapsedSearchResult>
    </InfiniteScroll>
  );
}

export default ClassSearchResult;
